import React from 'react';
import { sanitize } from 'dompurify';
/* eslint-disable no-restricted-imports */
import { Path, Link as RouterLink, To, useLocation as useLocation } from 'react-router-dom';
/* eslint-enable no-restricted-imports */
import LaunchIcon from 'mdi-material-ui/Launch';
import {
  Box,
  combineSx,
  Tab,
  TabProps,
  Link as MuiLink,
  LinkProps as MuiLinkProps,
  LinkTypeMap,
} from '@chronosphereio/chrono-ui';
import { AppError, useFeatureFlags } from '@chronosphereio/core';
import { logError } from './errors/error-utils';

export type LinkProps = MuiLinkProps<typeof RouterLink> & {
  /**
   * When true, allows the link to be activated with the space bar in addition to Enter.
   * By default, links follow WAI-ARIA standards and only activate with Enter.
   * Enable this for links that have button-like behavior for consistency.
   */
  activateOnSpace?: boolean;
};

const sanitizeUrl = (url: string) => {
  const allowedProtocols = ['http:', 'https:', 'mailto:'];
  try {
    const parsedUrl = new URL(url, window.location.origin);
    if (allowedProtocols.includes(parsedUrl.protocol)) {
      return sanitize(url);
    }
  } catch {
    return null;
  }

  return null;
};

/**
 * Wrapper around chrono-ui themed link that handles in-application routing. "to" prop is required.
 * Use if you are linking to any route inside of the Chronosphere SPA.
 * @param {string} to In-application route location
 * @returns {React.Component} Rendered MUI Link
 */
export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  { children, state, to = '', activateOnSpace, onKeyDown, ...props },
  ref
) {
  const location = useLocation();
  const { clientSideTracing } = useFeatureFlags();

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      // Call user's onKeyDown handler if provided
      onKeyDown?.(event);

      if (activateOnSpace && event.key === ' ' && !event.defaultPrevented) {
        event.preventDefault(); // Prevent page scroll
        event.currentTarget.click(); // Trigger navigation
      }
    },
    [activateOnSpace, onKeyDown]
  );

  try {
    let linkTo: To | null = '';

    if (typeof to === 'string') {
      linkTo = sanitizeUrl(to);
    }

    // If we cannot generate a safe URL, just return the text of the link with no href
    if (linkTo === null) {
      return <>{children}</>;
    }

    linkTo = typeof to === 'string' ? new URL(to, window.location.origin) : (to as Partial<Path>);

    return (
      <MuiLink
        ref={ref}
        component={RouterLink}
        to={{ pathname: linkTo.pathname, search: linkTo.search }}
        state={{ ...(state ?? {}), from: location.pathname }}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {children}
      </MuiLink>
    );
  } catch (e) {
    logError(
      new AppError({ severity: 'warning', title: 'bad link generation', rootCause: e as Error }),
      clientSideTracing,
      undefined,
      'bad link generation'
    );

    // Log the error, but return a valid link anyway, falling back to the default 'to' behavior
    return (
      <MuiLink ref={ref} component={RouterLink} to={to} onKeyDown={handleKeyDown} {...props}>
        {children}
      </MuiLink>
    );
  }
});

export type ExternalLinkProps = LinkTypeMap['props'] & {
  href: string;
  identifyInternalRoutes?: boolean;
  /**
   * When true, allows the link to be activated with the space bar in addition to Enter.
   * By default, links follow WAI-ARIA standards and only activate with Enter.
   * Enable this for links that have button-like behavior for consistency.
   */
  activateOnSpace?: boolean;
};

/**
 * Wrapper around chrono-ui themed link with the addition of an "external link" icon. "href" is required.
 * Use if you are linking outside of the Chronosphere SPA.
 * @param {string} href String URL provided to the underlying anchor element
 * @returns {React.Component} Rendered MUI Link
 */
export const ExternalLink = React.forwardRef<HTMLAnchorElement, ExternalLinkProps>(function ExternalLink(
  { children, href, sx, identifyInternalRoutes = false, activateOnSpace, onKeyDown, ...props }: ExternalLinkProps,
  ref
) {
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      // Call user's onKeyDown handler if provided
      onKeyDown?.(event);

      if (activateOnSpace && event.key === ' ' && !event.defaultPrevented) {
        event.preventDefault(); // Prevent page scroll
        event.currentTarget.click(); // Trigger navigation
      }
    },
    [activateOnSpace, onKeyDown]
  );

  const cleanedUrl = sanitizeUrl(href);

  // If we cannot generate a safe URL, just return the text of the link with no href
  if (cleanedUrl === null) {
    return (
      <Box
        sx={combineSx(
          {
            display: 'inline-flex',
            flexDirection: 'row',
            alignItems: 'center',
          },
          sx
        )}
      >
        {children}
      </Box>
    );
  }

  // Check to see if the external link is actually an internal link
  // In which case use regular Link to preserve query param states
  const urlObj = new URL(href, window.location.origin);
  if (identifyInternalRoutes && urlObj.host === window.location.host) {
    return (
      <Link {...props} to={href} ref={ref} activateOnSpace={activateOnSpace} onKeyDown={onKeyDown}>
        {children}
      </Link>
    );
  }

  return (
    <MuiLink
      ref={ref}
      href={cleanedUrl}
      component="a"
      onKeyDown={handleKeyDown}
      {...props}
      sx={combineSx(
        {
          display: 'inline-flex',
          flexDirection: 'row',
          alignItems: 'center',
        },
        sx
      )}
      target="_blank"
      rel="noopener"
    >
      {children}
      <LaunchIcon fontSize="inherit" sx={{ marginLeft: (theme) => theme.spacing(0.5) }} />
    </MuiLink>
  );
});

const LinkAdapter = React.forwardRef<typeof Link, LinkProps>(({ href, ...theRest }, _ref) => {
  return href === undefined ? <Link {...theRest} /> : <Link {...theRest} to={href} />;
});
LinkAdapter.displayName = 'LinkAdapter';

/**
 * Tab that uses a <Link /> in order to navigate without a full page reload
 */
export function LinkTab<T extends string | number>(props: TabProps<T>) {
  return <Tab<T> LinkComponent={LinkAdapter} {...props} />;
}

export type LinkButtonProps = Omit<LinkTypeMap['props'], 'href'> & {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
};

/**
 * LinkButton to provide onClick functionality that has the styling of an anchor element. "onClick" prop
 * is required. Use if you do not have a meaningful href
 * @param {function} onClick Event handler called when the LinkButton is clicked
 * @returns {React.Component} Rendered MUI Link as a button
 */
export const LinkButton = React.forwardRef<HTMLButtonElement, LinkButtonProps>(function LinkButton(
  { children, onClick, ...props },
  ref
) {
  return (
    <MuiLink ref={ref} onClick={onClick} type="button" component="button" fontSize="inherit" {...props}>
      {children}
    </MuiLink>
  );
});

/**
 * For rendering text that's styled like a Link, but doesn't actually Link to
 * anything. Can be useful if, for example, you have another component like
 * a ListItemButton that's _actually_ the clickable thing with an href, but you
 * want the button's text to look like a Link.
 */
export function LinkText({ children, ...others }: Pick<MuiLinkProps<'span'>, 'children' | 'sx' | 'className'>) {
  return (
    <MuiLink {...others} component="span">
      {children}
    </MuiLink>
  );
}
